import { signS3Inventory } from "@api/SignS3";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_INVENTORY } from "@components/notes/edit/api/mutations";
import {
  faSpinner,
  faFileUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GET_INVENTORIES } from "@graphql/queries/GetInventories";
import axios from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useRef, useState } from "react";

import { useWatchUserWsEvent } from "../../../../hooks";
import { alert } from "../../../common/Alert";
import CompanionSettingWrapper from "../shared/CompanionSettingWrapper";

const MAX_INVENTORY_SIZE_MB = 10;

const InventoryUpload = () => {
  const [createInventory] = useMutation(CREATE_INVENTORY);

  const [isUploading, setIsUploading] = useState(false);
  const { data, refetch } = useQuery(GET_INVENTORIES);
  const { billingCompanion } = useFlags();

  useWatchUserWsEvent({
    onMessage: refetch,
    eventName: "user_update",
  });

  const handleFileInputChange = async (e) => {
    const { files } = e.target;
    if (!files.length) return;
    const file = files[0];
    await handleFileUpload(file);
  };

  const handleFileUpload = async (file) => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_INVENTORY_SIZE_MB) {
      mixpanel.track("Inventory Upload Failed", {
        fileSizeMB,
        error: "File too large",
      });
      alert(
        "error",
        `File is too large (max: ${MAX_INVENTORY_SIZE_MB}MB)`,
      );
      return;
    }

    setIsUploading(true);

    try {
      const fileType = file.name.split(".").pop();
      const signS3Response = await signS3Inventory(fileType);

      const { data, storageObjectName } = signS3Response.data;
      const { fields } = data;

      const formData = new FormData();
      for (const [key, value] of Object.entries(fields)) {
        formData.append(key, value);
      }

      formData.append("file", file);
      formData.append("Content-Type", file.type);

      await axios.post(data.url, formData, {
        headers: {
          "Access-Control-Allow-Origin": true,
        },
      });

      await createInventory({
        variables: {
          storageObjectName,
        },
      });

      refetch();

      alert("success", "Inventory uploaded");
    } catch (error) {
      mixpanel.track("Inventory Upload Failed", {
        fileSizeMB,
        error: error.message,
      });
      alert(
        "error",
        "Failed to upload your inventory. Please try again.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const inventories = data?.user?.inventories || [];
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  if (!billingCompanion) {
    return null;
  }

  return (
    <CompanionSettingWrapper
      title="Inventory Upload"
      description="Upload your inventory file to enable inventory-related features"
    >
      <div className="flex-1 flex mr-4 md:space-x-4">
        <div className="flex-1 divide-y dark:divide-gray-700">
          <div className="m-2">
            <div
              onClick={() => {
                fileInputRef.current.click();
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center border border-dashed border-2 rounded-xl p-4 gap-1 cursor-pointer transition-colors ${
                isDragOver && "border-indigo-500 bg-indigo-50"
              }`}
              style={{ borderDasharray: "8 4" }}
            >
              {isUploading ? (
                <>
                  Uploading...
                  <FontAwesomeIcon
                    className="ml-2 animate-spin"
                    icon={faSpinner}
                  />
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    className="text-indigo-500 text-3xl"
                    icon={faFileUpload}
                  />
                  <b className="text-indigo-500 text-lg">
                    Upload a file
                  </b>
                  <span className="text-sm">
                    Drag and drop or click to select
                  </span>
                </>
              )}
            </div>
          </div>

          <input
            type="file"
            hidden
            ref={fileInputRef}
            accept={[
              "application/pdf",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ].join(",")}
            onChange={handleFileInputChange}
            data-testid="companion-upload-input"
          />

          {inventories.map((inventory) => {
            const date = moment(inventory.createdAt).format(
              "MMMM Do YYYY, h:mma",
            );
            const diff = moment(inventory.createdAt).fromNow();

            return (
              <div
                key={inventory.inventoryUuid}
                className="flex justify-between items-center py-2"
                data-testid={`companion-inventory-status-${inventory.status}`}
              >
                <span className="text-sm">
                  {date} ({diff})
                </span>
                <span className="text-sm font-medium">
                  {inventory.status}
                  {inventory.status === "PROCESSING" && (
                    <FontAwesomeIcon
                      className="ml-2 animate-spin"
                      icon={faSpinner}
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </CompanionSettingWrapper>
  );
};

export default InventoryUpload;
